import { Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import React from "react";
import { flattenObject } from "../../../lib/flattenObject";
import {
  borrowerSummary,
  FlattenedRentalQuote,
  getSummaryTerms,
  propertySummary,
  summaryTerms,
  TermSheetMap,
} from "../../../lib/models/rental/rental-summary-term-sheet-map";
import { RentalQuote, Scenario } from "../../../lib/models/rental/rental.model";
import { SFUserProfile } from "../../../lib/models/sf-user-profile.model";
import { PdfGenerationActions } from "../../../lib/types/PDFGenerationActions";
import { QuoteMetadata } from "../../../lib/types/QuoteMetadata";
import { checkIfDecimal } from "../../../lib/utils";
import pdfLogo from "../../../limaone_logo.png";

export interface RentalSummarySheetProps {
  quote: RentalQuote;
  quoteCalculation: { [key: string]: any } | { [key: number]: any };
  rentalScenarios: Scenario[];
  quoteDate: string;
  action: PdfGenerationActions;
  userProfile: SFUserProfile;
  metadata: QuoteMetadata;
}

function RentalSummarySheet(props: RentalSummarySheetProps) {
  const {
    quote,
    quoteCalculation,
    rentalScenarios,
    quoteDate,
    action,
    userProfile,
  } = props;

  const borrowerSummaryValues: {
    numOfProperties: number | undefined;
    quoteDate: string | undefined;
  } = {
    quoteDate: quoteDate,
    numOfProperties: quote.numOfProperties,
  };

  const flattenedRentalQuote: FlattenedRentalQuote = React.useMemo(() => {
    return flattenObject({
      quote,
      calculation: (quoteCalculation as { [key: number]: any })[0],
    }) as FlattenedRentalQuote;
  }, [quote, quoteCalculation]);

  const sheetTitle: Array<string> = React.useMemo(() => {
    return (quoteCalculation as { [key: number]: any })[0].eocTitle.split("-");
  }, [quoteCalculation]);

  const renderRowLabel = (row: TermSheetMap<FlattenedRentalQuote>) => {
    if (row.showLabelAltOnCondition && row.accessor) {
      const value = flattenedRentalQuote[row.accessor] as number;
      return row.showLabelAltOnCondition(value) ? row.labelAlt : row.label;
    }

    return row.label;
  };

  return (
    <Page size="LETTER" style={{ borderWidth: 4 }}>
      <View>
        <View style={styles.header}>
          <View>
            <Text style={[text.base, text.lineHeightNormal, text.lg]}>
              <Text style={[text.bold, text.uppercase, text.blue]}>
                Rental Loan -
              </Text>
              <Text> {sheetTitle[1]}</Text>
            </Text>
            <Text
              style={[
                text.base,
                { fontSize: 14 },
                text.bold,
                text.uppercase,
                text.lineHeightNormal,
              ]}
            >
              Preliminary Cost Estimates
            </Text>
          </View>
          {action !== "WHITELABEL" && (
            <View>
              <Image src={pdfLogo} style={[styles.headerImage]} />
            </View>
          )}
        </View>

        <View style={styles.summaryInfoWrapper}>
          <View style={styles.borrowerSummaryContainer}>
            <View>
              <Text
                style={[
                  text.base,
                  text.white,
                  text.bold,
                  text.lineHeightNormal,
                ]}
              >
                {flattenedRentalQuote.borrowingEntity}
              </Text>
            </View>

            <View>
              {borrowerSummary.map((row, index) => (
                <View
                  key={index}
                  style={[styles.row, { justifyContent: "space-between" }]}
                >
                  <View style={styles.summaryColLabel}>
                    <Text style={[text.sm, text.white, text.lineHeightNormal]}>
                      {row.label}
                    </Text>
                  </View>
                  <View>
                    <Text
                      style={[
                        text.sm,
                        text.white,
                        text.lineHeightNormal,
                        { textAlign: "right" },
                      ]}
                    >
                      {row.transformer
                        ? row.accessor &&
                          row.transformer(borrowerSummaryValues[row.accessor])
                        : row.accessor && borrowerSummaryValues[row.accessor]}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.propertySummaryContainer}>
            {propertySummary.map((row, index) =>
              index === 0 ? (
                <View key={index} style={[styles.row]}>
                  <View style={[styles.summaryColLabel]}>
                    <Text
                      style={[
                        text.base,
                        text.white,
                        text.bold,
                        text.lineHeightNormal,
                      ]}
                    >
                      {renderRowLabel(row)}
                    </Text>
                  </View>
                  <View style={styles.col}>
                    <Text
                      style={[
                        text.sm,
                        text.white,
                        text.alignRight,
                        text.bold,
                        text.lineHeightNormal,
                      ]}
                    >
                      {row.transformer
                        ? row.accessor &&
                          row.transformer(flattenedRentalQuote[row.accessor])
                        : row.accessor && flattenedRentalQuote[row.accessor]}
                    </Text>
                  </View>
                </View>
              ) : (
                <View
                  key={index}
                  style={[styles.row, { marginTop: index === 1 ? 8 : 0 }]}
                >
                  <View style={[styles.summaryColLabel]}>
                    <Text style={[text.sm, text.white, text.lineHeightNormal]}>
                      {renderRowLabel(row)}
                    </Text>
                  </View>
                  <View style={styles.col}>
                    <Text
                      style={[
                        text.sm,
                        text.white,
                        text.alignRight,
                        text.lineHeightNormal,
                      ]}
                    >
                      {row.transformer
                        ? row.accessor &&
                          row.transformer(flattenedRentalQuote[row.accessor])
                        : row.accessor && flattenedRentalQuote[row.accessor]}
                    </Text>
                  </View>
                </View>
              )
            )}
          </View>
        </View>
        <View
          style={[text.alignCenter, { padding: 8 }, { fontStyle: "italic" }]}
        >
          <Text style={[text.base, text.xs]}>
            The above information was used to calculate your quote
          </Text>
        </View>
        <View style={styles.calculationTable}>
          <View style={styles.summaryRow}>
            <View style={[styles.summaryCol, styles.summaryColLabel]}>
              <Text style={[text.base, text.bold, text.blue]}>
                Summary Terms
              </Text>
            </View>

            {rentalScenarios.map((scenario, index) => (
              <View
                key={scenario.scenarioId}
                style={[styles.summaryCol, { position: "relative" }]}
              >
                {rentalScenarios.length > 2 && index % 2 !== 0 && (
                  <View style={[styles.centerStripe]}></View>
                )}

                <Text style={[text.base, text.bold]}>
                  {checkIfDecimal(scenario.ltvPercentage)}% LTV
                </Text>
              </View>
            ))}
          </View>

          {getSummaryTerms(
            flattenedRentalQuote.closingCashToBorrower,
            flattenedRentalQuote.campaignFeeIncentive,
            summaryTerms
          ).map((row, index) => (
            <View key={index} style={[styles.summaryRow, { marginTop: 8 }]}>
              <View style={[styles.summaryCol, styles.summaryColLabel]}>
                <Text
                  style={
                    row.label === "Promotional Credit"
                      ? text.promotionText
                      : text.base
                  }
                >
                  {renderRowLabel(row)}
                </Text>
              </View>

              {rentalScenarios.map((scenario) => (
                <View key={scenario.scenarioId} style={styles.summaryCol}>
                  <Text
                    style={
                      row.label === "Promotional Credit"
                        ? text.promotionText
                        : text.base
                    }
                  >
                    {row.transformer
                      ? row.accessor &&
                        row.transformer(
                          (quoteCalculation as { [key: number]: any })[
                            scenario.scenarioId - 1
                          ][row.accessor]
                        )
                      : row.accessor &&
                        (quoteCalculation as { [key: number]: any })[
                          scenario.scenarioId - 1
                        ][row.accessor]}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        <View style={styles.footer}>
          {action !== "WHITELABEL"
            ? userProfile && (
                <View style={styles.signatureContainer}>
                  <Text style={[text.base, text.xs, text.uppercase]}>
                    {userProfile.fullName}
                  </Text>
                  <Text style={[text.base, text.xs]}>{userProfile.title}</Text>
                  <Text style={[text.base, text.xs, { marginTop: 8 }]}>
                    P: {userProfile.phone}
                  </Text>
                  <Text style={[text.base, text.xs]}>
                    Email: {userProfile.email}
                  </Text>
                  <Text style={[text.base, text.xs]}>URL: limaone.com</Text>
                </View>
              )
            : null}
        </View>
      </View>
    </Page>
  );
}

export default RentalSummarySheet;

const styles = StyleSheet.create({
  header: {
    padding: "16 16 8 16",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerImage: {
    height: "auto",
    width: 150,
    marginRight: 16,
  },
  summaryInfoWrapper: {
    display: "flex",
    flexDirection: "row",
  },
  borrowerSummaryContainer: {
    backgroundColor: "#203864",
    marginRight: 2,
    padding: "16 16 8 16",
    flex: "1 0 39%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  propertySummaryContainer: {
    backgroundColor: "#4472C4",
    marginLeft: 2,
    padding: "16 16 8 16",
    flex: "1 0 59%",
  },
  calculationTable: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 0,
    paddingBottom: 8,
  },
  centerStripe: {
    position: "absolute",
    top: -8,
    right: -10,
    height: "54vh",
    width: "85%",
    borderRadius: 3,
    backgroundColor: "#f2f6fa",
  },
  summaryRow: {
    display: "flex",
    flexDirection: "row",
    textAlign: "right",
  },
  summaryCol: {
    flex: "2 1 auto",
  },
  summaryColLabel: {
    flex: "2 0 auto",
    textAlign: "left",
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    borderTopWidth: 1,
    borderColor: "#DBDBDB",
    justifyContent: "space-between",
    paddingTop: 8,
    margin: "0 16",
  },
  disclosureContainer: {
    flex: "1 0 55%",
    marginRight: 16,
  },
  signatureContainer: {
    flex: "1 0 40%",
  },
  row: {
    display: "flex",
    flexDirection: "row",
  },
  col: {
    flex: 1,
  },
});

const text = StyleSheet.create({
  base: {
    fontFamily: "Roboto",
    color: "#000000",
    fontSize: 16,
    position: "relative",
    lineHeight: 1.8,
  },

  lg: { fontSize: 18 },
  sm: { fontSize: 12 },
  xs: { fontSize: 10 },
  xxs: { fontSize: 8 },

  bold: { fontWeight: 700 },

  darkBlue: { color: "#203864" },
  blue: { color: "#0070C0" },
  white: { color: "white" },

  alignRight: { textAlign: "right" },
  uppercase: { textTransform: "uppercase" },
  lineHeightNormal: { lineHeight: 1.5 },
  alignCenter: { textAlign: "center" },
  promotionText: {
    color: "#01457C",
    fontWeight: 700,
    fontSize: 16,
    position: "relative",
    lineHeight: 1.8,
  },
});
